// The full reminders list: reading it, and ticking one off from it.
//
// A read of its own, unlike the block on the day. The day payload carries the
// reminders that were open on ONE day; this screen is every reminder there is,
// finished ones included, which is a different question and therefore a
// different request - `GET /routines?sectionId=none`, the same resource with
// one query value, because a reminder is a routine with no schedule and two
// URLs for one resource would be two places for that to be forgotten.
//
// It keeps whatever it last had while the next read is in flight, deliberately:
// emptying it would flash "Loading…" over the whole list every time something
// is written, which is a flash of nothing in exchange for no information. The
// day screen's own cache and the schedules list in Settings make the same trade
// for the same reason.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { IsoDay } from '../api/client.ts';
import { reminderFromRoutine } from './reminders-model.ts';
import type { ReminderView } from './reminders-model.ts';

export interface RemindersState {
  /** Null until the first read lands. Kept through every read after that. */
  rows: ReminderView[] | null;
  /** Why the last read did not land. The rows above may still be right. */
  problem: string | null;
  /**
   * Whether a tick can be written at all. False only while the account's today
   * is unknown, which is the day a tick here is recorded against - and no
   * client works out the account's day (AGENTS.md).
   */
  canToggle: boolean;
  toggle: (id: string) => void;
}

export interface RemindersHost {
  /** Bumped by every write the editor makes, and by a tick here. */
  revision: number;
  /** The account's today, from the day payload. Null while it is on its way. */
  today: IsoDay | null;
  onSignedOut: () => void;
  /** A transient line at the foot of the screen. */
  say: (message: string) => void;
  /**
   * Said after a tick, so the day screen's block catches up with it.
   *
   * A tick on the day screen does not do this - the block it moves is the one
   * already on screen - but a tick HERE moves a row on a screen that is not
   * mounted, and a person who ticks something off and then presses Today must
   * not find it still sitting there open.
   */
  refresh: () => void;
}

export function useReminders({
  revision,
  today,
  onSignedOut,
  say,
  refresh,
}: RemindersHost): RemindersState {
  const [rows, setRows] = useState<ReminderView[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // The effect depends on WHICH read is wanted, not on how the caller is
  // rendered: these are fresh closures every render, and depending on them
  // would re-read the list on every keystroke anywhere in the shell.
  const latest = useRef({ onSignedOut, say, refresh });
  latest.current = { onSignedOut, say, refresh };

  // Ticks made here that the server has not confirmed yet, and how many have
  // been started at all.
  //
  // THE INVARIANT IS: DO NOT APPLY A READ WHILE ANY LOCAL WRITE IS OUTSTANDING.
  // A payload assembled while a tick was in flight does not carry that tick, so
  // applying it draws the ticked row back open until the write's own re-read
  // lands - the row un-ticks and re-ticks under the finger that ticked it. That
  // is NOT the same rule as "nothing painted since the read went out": a tick
  // made BEFORE the read was asked for is still unconfirmed while its write is
  // in flight, and ticking two rows inside one round trip reaches exactly that
  // ordering - the first write returns, refreshes the list, and the read goes
  // out while the second write is still outstanding. `useDayScreen` keeps these
  // same two counts in its `writes` record, one pair PER DAY, because a day
  // payload can only clobber its own day - so this is one rule and not two. Its
  // `inFlight` ref is a different question: which days have a READ out.
  const writesOut = useRef(0);
  const writesBegun = useRef(0);

  useEffect(() => {
    let live = true;
    // Both stamped on the way OUT: a write already in flight, and one begun
    // while this read is waiting, each mean the payload can predate the screen.
    const outstanding = writesOut.current;
    const begun = writesBegun.current;
    void (async () => {
      try {
        const routines = await api.routines({ sectionId: 'none' });
        if (!live || outstanding > 0 || writesBegun.current !== begun) return;
        // Mapped, never sorted: the order is the server's, and it is the same
        // order the block on the day draws (`reminders-model.ts`).
        setRows(routines.map(reminderFromRoutine));
        setProblem(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current.onSignedOut();
          return;
        }
        if (live) setProblem(messageFor(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [revision]);

  const toggle = useCallback(
    (id: string) => {
      if (today === null) return;
      const current = rows?.find((r) => r.id === id);
      if (current === undefined) return;

      const next = !current.done;
      // A reminder's completion day is the day it was DONE - an event - so
      // ticking one here records TODAY, while unticking removes the row on the
      // day it was actually finished, which may be neither today nor the day
      // this screen was opened on. The block on the day never has to ask: there
      // the day being looked at is the answer to both.
      const day: IsoDay = next ? today : (current.completedDay ?? today);

      const paint = (done: boolean, completedDay: IsoDay | null) =>
        setRows(
          (held) => held?.map((r) => (r.id === id ? { ...r, done, completedDay } : r)) ?? held,
        );

      paint(next, next ? day : null);

      // Raised BEFORE the request, never after it: a read asked for in between
      // would find a count of zero and go on to overwrite the paint above.
      writesOut.current += 1;
      writesBegun.current += 1;

      void (async () => {
        let gone = false;
        try {
          if (next) await api.complete(id, day);
          else await api.uncomplete(id, day);
        } catch (error) {
          // Put back exactly what the tick moved, so nothing on screen claims
          // to have been saved. Offline writes are out of scope for v1: there
          // is no queue and no retry.
          paint(current.done, current.completedDay);
          gone = isSignedOut(error);
          if (gone) latest.current.onSignedOut();
          else latest.current.say(`Not saved. ${messageFor(error)}`);
        } finally {
          // Lowered however it ended, and before the refresh below: the read
          // that refresh asks for is only safe once nothing is unconfirmed.
          writesOut.current -= 1;
        }
        // HOWEVER IT ENDED, not only when it worked. On success the day screen
        // holds this reminder too, on a screen nobody is looking at right now.
        // On failure this is what asks again for the read the guard above threw
        // away: a create or a Remove refreshes, and a tick started before that
        // read landed drops it - so without this the list goes on showing a
        // reminder the toast just said was removed until the screen is left and
        // come back to. The one exception is a session that has gone, where the
        // gate has replaced the screen and a read would only be a doomed one.
        if (!gone) latest.current.refresh();
      })();
    },
    [rows, today],
  );

  return { rows, problem, canToggle: today !== null, toggle };
}
